import {
    PlusIcon,
    TrashIcon
} from "@heroicons/react/24/outline";

import {
    CONTEXT_FACT_ENTITY,
    CONTEXT_FACT_ENTITIES
} from "../lib/consts";
import { IContextFact } from "../lib/types";

import { Button } from "./Button";
import { Textbox } from "./Textbox";
import { QuestionMarkCircleIcon } from "@heroicons/react/20/solid";
import { Tooltip } from "react-tooltip";
import { timezones } from "../lib/consts";

type TemplateFactsProps = {
    facts: IContextFact[];
    setFacts: (facts: IContextFact[]) => void;
    guidelines?: string[];
    setGuidelines?: (guidelines: string[]) => void;
    disabled?: boolean;
};

export function TemplateFacts(props: TemplateFactsProps) {
    const { facts, setFacts, guidelines, setGuidelines, disabled } = props;
    const has_current_date_fact = facts.some(fact => fact.entity === CONTEXT_FACT_ENTITY.current_date);

    const addFact = (entity?: string) => {
        setFacts([...facts, { entity: entity ? entity : "", role: "" }]);
    };

    const addCurrentDateFact = () => {
        // get browser timezone
        const browser_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        // find the timezone in the timezones array so by default the browser's timezone is used
        const initial_timezone = timezones.find(tz => tz === browser_timezone) || timezones[0];
        // add the current date fact to the facts array
        setFacts([...facts, { entity: CONTEXT_FACT_ENTITY.current_date, role: "current_date_placeholder", timezone: initial_timezone }]);
    };

    const delFact = (idx: number) => {
        setFacts(facts.filter((_, i) => i !== idx));
    }

    const editFactEntity = (idx: number, entity: string) => {
        const newFacts = [...facts];
        newFacts[idx].entity = entity;
        setFacts(newFacts);
    };

    const editFactRole = (idx: number, role: string) => {
        const newFacts = [...facts];
        newFacts[idx].role = role;
        setFacts(newFacts);
    };

    const updateTimezone = (idx: number, timezone: string) => {
        const newFacts = [...facts];
        newFacts[idx].timezone = timezone;
        setFacts(newFacts);
    };

    const addGuideline = () => {
        if (setGuidelines) {
            setGuidelines([...(guidelines || []), ""]);
        }
    };

    const updateGuideline = (idx: number, value: string) => {
        if (setGuidelines && guidelines) {
            const newGuidelines = [...guidelines];
            newGuidelines[idx] = value;
            setGuidelines(newGuidelines);
        }
    };

    const deleteGuideline = (idx: number) => {
        if (setGuidelines && guidelines) {
            setGuidelines(guidelines.filter((_, i) => i !== idx));
        }
    };

    const onGuidelineButton = (idx: number) => {
        if (guidelines && (idx < guidelines.length)) {
            deleteGuideline(idx);
        } else {
            addGuideline();
        }
    };

    return (
        <div className="max-w-5xl sm:items-start sm:gap-4 sm:py-6 text-gray-600 text-sm">
            {/* Guidelines Section */}
            {setGuidelines && (
                <div className="pb-6 border-b border-gray-200">
                    <div className="flex flex-row items-center mb-2">
                        <p className="pr-2 font-medium leading-6 text-gray-900 my-auto">
                            Template Guidelines
                        </p>
                        <QuestionMarkCircleIcon
                            className="w-5 h-5 ml-1 text-gray-400"
                            data-tooltip-id="guidelines-tooltip-id"
                            data-tooltip-html={
                                `<p class="pb-4 max-w-sm">
                                    General guidelines for extracting data from this template.
                                </p>
                                <p class="pb-4 max-w-sm">
                                    These guidelines will be included in the extraction prompts to help the AI understand
                                    how to process documents for this template.
                                </p>
                                <p class="max-w-sm">
                                    For example: "Always extract dates in YYYY-MM-DD format" or "Ignore any text in parentheses".
                                </p>`
                            }
                        />
                        <Tooltip id="guidelines-tooltip-id" />
                    </div>
                    <div className="space-y-2">
                        {(Array.isArray(guidelines) && guidelines.length > 0 ? [...guidelines, ""] : [""]).map((guideline, idx) => (
                            <div key={idx} className="flex w-full">
                                <div className="flex-grow flex flex-row items-center">
                                    <span className="text-sm pl-2 w-9">{idx + 1}.</span>
                                    <Textbox
                                        value={guideline}
                                        placeholder="Enter a guideline for extraction..."
                                        onChange={(value) => updateGuideline(idx, value)}
                                        disabled={disabled}
                                    />
                                </div>
                                <Button
                                    icon={Array.isArray(guidelines) && guidelines.length > 0 && idx < guidelines.length ? TrashIcon : PlusIcon}
                                    onClick={() => onGuidelineButton(idx)}
                                    disabled={disabled}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Static Facts Section */}
            <div className="pb-4 flex flex-row items-center">
                <p className="pr-2 font-medium leading-6 text-gray-900 my-auto">
                    Static Facts
                </p>
                <QuestionMarkCircleIcon
                    className="w-5 h-5 ml-1 text-gray-400"
                    data-tooltip-id="facts-tooltip-id"
                    data-tooltip-html={
                        `<p class="pb-4 max-w-sm">
                            Statements about entities that are always true when extracting this template.
                        </p>
                        <p class="pb-4 max-w-sm">
                            For example, when creating a template for processing supplier invoices, you might
                            include a static fact stating the name of your company and affirming that your company
                            is always the recipient of the invoice.
                        </p>
                        <p class="max-w-sm">
                            Similarly, when processing emails, you can define the role of the individual who forwarded
                            the email to the system. For instance, in the context of processing supplier invoices, a static
                            fact might state that the person forwarding the email is always considered the approver.
                        </p>`
                    }
                />
                <Tooltip id="facts-tooltip-id" />
                <div className="grow" />
                <Button icon={PlusIcon} text="Recipient Role" onClick={() => { addFact(CONTEXT_FACT_ENTITY.recipient); }} disabled={disabled} />
                <Button icon={PlusIcon} text="Current Date" onClick={addCurrentDateFact} disabled={disabled || has_current_date_fact} />
                <Button icon={PlusIcon} text="New Fact" onClick={() => { addFact(); }} disabled={disabled} />
            </div>
            {facts.map((fact, idx) => (<div key={idx} className="py-1 grid grid-cols-3 items-center">
                <div className="h-9">
                    {fact.entity === CONTEXT_FACT_ENTITY.sender && <label
                        htmlFor="title"
                        className="my-2 text-sm font-medium leading-6 text-gray-900"
                        title="Person from outside that sent the email to the recipient">
                        Email Sender
                    </label>}
                    {fact.entity === CONTEXT_FACT_ENTITY.recipient && <label
                        htmlFor="title"
                        className="my-2 text-sm font-medium leading-6 text-gray-900"
                        title="Person who forwarded the email to the system">
                        Email Recipient
                    </label>}
                    {fact.entity === CONTEXT_FACT_ENTITY.current_date && <input
                        type="text"
                        className="block w-full h-9 shadow-sm sm:text-sm focus:ring-space_blue-500 focus:border-space_blue-500 border-gray-300 rounded-md bg-gray-50"
                        value="Current Date"
                        readOnly
                    />}
                    {!CONTEXT_FACT_ENTITIES.includes(fact.entity) && <input
                        type="text"
                        className="block w-full h-9 shadow-sm sm:text-sm focus:ring-space_blue-500 focus:border-space_blue-500 border-gray-300 rounded-md"
                        placeholder="entity"
                        value={fact.entity}
                        onChange={(e) => { editFactEntity(idx, e.target.value); }} />}
                </div>
                <div className="ml-2 col-span-2 flex h-9">
                    {fact.entity === CONTEXT_FACT_ENTITY.current_date ? (
                        <select
                            className="block w-full shadow-sm sm:text-sm focus:ring-space_blue-500 focus:border-space_blue-500 border-gray-300 rounded-md"
                            value={fact.timezone}
                            onChange={(e) => updateTimezone(idx, e.target.value)}
                        >
                            {timezones.map((timezone) => (
                                <option key={timezone} value={timezone}>
                                    {timezone}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <input
                            type="text"
                            className="block w-full shadow-sm sm:text-sm focus:ring-space_blue-500 focus:border-space_blue-500 border-gray-300 rounded-md"
                            placeholder="role"
                            value={fact.role}
                            onChange={(e) => { editFactRole(idx, e.target.value); }} />
                    )}
                    <Button icon={TrashIcon} onClick={() => { delFact(idx); }} disabled={disabled} />
                </div>
            </div>))}
        </div>
    );
}